/**
 * `unsupported` — the fall-back definition for a column type we do not model yet
 * (`specs/M1-B5-column-type-framework.md` acceptance R1, §10 case 11).
 *
 * monday's enum has 43 column types and 34 are in use on this account; tier 1 implements 13.
 * Without a fall-back, `getColumnType('timeline')` throws and one unmodelled column takes a
 * whole board read down. That is the wrong failure: the importer must never drop data
 * (doc 06), and `/v2` must keep serving the columns it *does* understand.
 *
 * So this definition is a **lossless passthrough**: `value` is stored and re-emitted exactly as
 * monday sent it, including every key we do not recognise, so a re-export is byte-identical.
 * What it cannot reproduce is `text`: monday computes that server-side from semantics we have
 * not modelled, and inventing one would violate CLAUDE.md rule 3. `toText` therefore returns
 * the recorded text, which the importer preserves in `cell_values.text_value` and the read path
 * passes back through `ColumnValueContext.storedText` — never a text we computed ourselves.
 *
 * It is deliberately `writable: true`: the importer, the conformance fixture builder and a
 * re-export all have to be able to store these cells. The **web** side renders it read-only
 * (there is no editor for a type we cannot model), which is what R1's "renders `text`
 * read-only" means.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { defineColumnType } from '../types.js';
export const unsupportedValueSchema = z.unknown();
export const unsupportedSettingsSchema = z.looseObject({});
function passthrough(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string' && raw.trim() === '')
        return null;
    // An empty object is monday's third clear form for several types `[09§A.7 rule 4]`.
    if (typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length === 0)
        return null;
    return raw;
}
import { unsupportedFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
function stableStringify(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value))
        return `[${value.map(stableStringify).join(',')}]`;
    const record = value;
    const keys = Object.keys(record).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(',')}}`;
}
export const unsupportedColumn = defineColumnType({
    type: 'unsupported',
    title: 'Unsupported',
    description: 'A monday column type this build does not model yet. Values round-trip untouched.',
    apiFragmentName: 'UnsupportedValue',
    legacyTypeName: 'unsupported',
    idPrefix: 'unsupported',
    valueSchema: unsupportedValueSchema,
    settingsSchema: unsupportedSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: unsupportedSettingsSchema,
    writable: true,
    supportsSimpleValue: false,
    clearForms: ['null', 'empty-string', 'empty-object'],
    // Nothing may be filtered on a shape we do not understand — the same rule monday applies to
    // `formula` and `mirror` `[09§A.7]`.
    filterConditions: unsupportedFilterConditions,
    filterOperators: operatorsOf(unsupportedFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'none'],
    emptyText: '',
    parseValue(raw) {
        return passthrough(raw);
    },
    toText(_value, _settings, ctx) {
        // We cannot reproduce monday's `text` for a type we do not model, and we never invent one
        // — but the importer preserved monday's own rendering in `cell_values.text_value`, and the
        // read path hands it back through `ctx.storedText`. Serving it is not invention: it is the
        // recorded value (T-X2 defect 3). With no stored text there is still nothing to say.
        return ctx?.storedText ?? '';
    },
    toSortKeys() {
        return { text: null, number: null, date: null };
    },
    isEqual(a, b) {
        return stableStringify(a ?? null) === stableStringify(b ?? null);
    },
    toActivityValue(value) {
        return value ?? null;
    },
    fromMondayValue(raw) {
        return passthrough(raw);
    },
    toMondayValue(value) {
        return value ?? null;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return unsupportedSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment() {
        // `UnsupportedValue` declares no extra fields beyond the `ColumnValue` interface `[sdl]`.
        return {};
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map